import type { Tables } from "@/integrations/supabase/types";

export type LeadStatus = "new" | "contacted" | "follow_up" | "voicemail" | "booked" | "closed" | "lost";

// DB row type
export type DbLead = Tables<"leads">;
export type DbProfile = Tables<"profiles">;

export function getScoreColor(score: number): string {
  if (score >= 85) return "text-hot";
  if (score >= 50) return "text-warning";
  return "text-muted-foreground";
}

export function getScoreLabel(score: number): string {
  if (score === 100) return "Emergency";
  if (score >= 85) return "Hot";
  if (score >= 50) return "Warm";
  return "Cold";
}

export function getRowColor(score: number): string {
  if (score === 100) return "bg-red-100 border-red-300";
  if (score >= 85) return "bg-red-50 border-red-200";
  if (score >= 50) return "bg-yellow-50 border-yellow-200";
  return "";
}

// ─── Fit Tier ──────────────────────────────────────────────────────────────
// `fit_tier` is computed by the Postgres function `compute_lead_fit()` and
// kept up-to-date by triggers. The frontend only READS it — never recomputes.
// Tiers describe how well a lead matches THIS agency's preferences (service
// area, specialties, payer mix, hours), independent of urgency/score.

export type FitTier = "hot_match" | "good_fit" | "possible_fit" | "low_fit";

export function getFitTier(lead: Pick<DbLead, "fit_tier">): FitTier | null {
  const t = (lead as any).fit_tier as string | null | undefined;
  if (t === "hot_match" || t === "good_fit" || t === "possible_fit" || t === "low_fit") return t;
  return null;
}

export const FIT_TIER_LABEL: Record<FitTier, string> = {
  hot_match: "Hot Match",
  good_fit: "Good Fit",
  possible_fit: "Possible Fit",
  low_fit: "Low Fit",
};

export const FIT_TIER_DESCRIPTION: Record<FitTier, string> = {
  hot_match: "Strong match across service area, specialty, payer, and hours — pursue first.",
  good_fit: "Matches your service area and at least one of specialty or payer.",
  possible_fit: "Some overlap with your preferences — review before pursuing.",
  low_fit: "Outside your service area or preferences. Consider broadcasting to the community.",
};

// Tailwind classes for the fit-tier pill. Uses semantic tokens.
export const FIT_TIER_BADGE_CLASS: Record<FitTier, string> = {
  hot_match: "bg-hot/10 text-hot border-hot/30",
  good_fit: "bg-success/10 text-success border-success/30",
  possible_fit: "bg-warning/10 text-warning border-warning/30",
  low_fit: "bg-muted text-muted-foreground border-border",
};

// Lead scoring lives exclusively in the Postgres function compute_lead_score().
// The DB triggers `set_lead_score_on_insert` / `set_lead_score_on_update` keep
// `leads.score` authoritative on every write. Edge functions call
// `score_lead_for_caller(lead_id)` to force a recompute. The frontend MUST
// only read `lead.score` — never recompute scores in TypeScript.

export const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "follow_up", label: "Follow Up" },
  { value: "voicemail", label: "Voicemail Left" },
  { value: "booked", label: "Booked" },
  { value: "closed", label: "Closed" },
  { value: "lost", label: "Lost" },
];
